import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, FolderOpen, Trash2, Tags, ArrowRight, Settings } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
// Imported directly from the projects module (not the "@/api" barrel) to keep this card's
// authorized file scope to projects.ts + Projects.tsx only — the barrel (api/index.ts) is
// intentionally left untouched.
import { projectsApi, type Project, type LlmProfile, type AnalysisAccountProfile, type AnalysisAccountProfileKey } from "@/api/projects";
import { ApiError } from "@/api/client";

const SYSTEM_DEFAULT_VALUE = "__system_default__";

/** Resolved-profile badge text. Always driven by backend-resolved fields, never inferred from project name. */
function formatLlmBadge(project: Project): string {
  if (project.llm_profile_source === "system_default") return "LLM: 系統預設";
  if (project.resolved_llm_profile_key === "openai_personal") return "LLM: OpenAI · Personal";
  if (project.resolved_llm_profile_key === "cometapi_company") return "LLM: CometAPI · Company";
  return "LLM: 系統預設";
}

function profileLabel(key: string, profiles: LlmProfile[]): string {
  if (key === "openai_personal") return "OpenAI · Personal";
  if (key === "cometapi_company") return "CometAPI · Company";
  return profiles.find(p => p.key === key)?.label || key;
}

export default function Projects() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [newAccountValue, setNewAccountValue] = useState<string>(SYSTEM_DEFAULT_VALUE);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: projects = [], isLoading } = useQuery<Project[]>({
    queryKey: ["projects"],
    queryFn: async () => {
      const res = await projectsApi.list();
      return res.data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: () => projectsApi.create({
      name: name.trim(),
      description: description.trim() || undefined,
      analysis_account_profile_key: newAccountValue === SYSTEM_DEFAULT_VALUE
        ? null
        : newAccountValue as AnalysisAccountProfileKey,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("專案已建立");
      setName(""); setDescription(""); setNewAccountValue(SYSTEM_DEFAULT_VALUE); setOpen(false);
    },
    onError: (err: Error) => toast.error(`建立失敗: ${err.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => projectsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("專案已刪除");
    },
    onError: (err: Error) => toast.error(`刪除失敗: ${err.message}`),
  });

  // ── LLM profile edit sheet ──────────────────────────────────────
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [selectedAccountValue, setSelectedAccountValue] = useState<string>(SYSTEM_DEFAULT_VALUE);
  const [profileError, setProfileError] = useState<string | null>(null);

  const { data: llmProfiles = [], isLoading: llmProfilesLoading } = useQuery<LlmProfile[]>({
    queryKey: ["llm-profiles"],
    queryFn: async () => {
      const res = await projectsApi.llmProfiles();
      return res.data || [];
    },
  });
  const { data: accountProfiles = [], isLoading: accountProfilesLoading } = useQuery<AnalysisAccountProfile[]>({
    queryKey: ["analysis-account-profiles"],
    queryFn: async () => (await projectsApi.analysisAccountProfiles()).data || [],
  });

  function openEditSheet(project: Project) {
    setEditingProject(project);
    setSelectedAccountValue(project.analysis_account_profile_key || SYSTEM_DEFAULT_VALUE);
    setProfileError(null);
  }

  function closeEditSheet() {
    setEditingProject(null);
    setProfileError(null);
  }

  const updateProfileMutation = useMutation({
    mutationFn: (vars: { project_id: string; analysis_account_profile_key: AnalysisAccountProfileKey | null; expected_updated_at: string | null }) =>
      projectsApi.updateAnalysisAccountProfile(vars.project_id, {
        analysis_account_profile_key: vars.analysis_account_profile_key,
        expected_updated_at: vars.expected_updated_at,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["analysis-account-profiles"] });
      toast.success("分析帳戶設定已儲存");
      setProfileError(null);
      if (res.data) {
        // Reflect the backend-resolved value immediately; re-syncs expected_updated_at
        // for any further save in the same sheet session.
        setEditingProject(res.data);
        setSelectedAccountValue(res.data.analysis_account_profile_key || SYSTEM_DEFAULT_VALUE);
      } else {
        closeEditSheet();
      }
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.status === 409) {
        setProfileError("專案設定已被他人變更，已重新整理，請確認後再試一次");
        queryClient.invalidateQueries({ queryKey: ["projects"] });
        return;
      }
      if (err instanceof ApiError && err.status === 422) {
        setProfileError(err.message || "此 Profile 尚未在伺服器設定，暫時無法選用");
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setProfileError("權限不足：無法修改分析帳戶設定");
        return;
      }
      setProfileError(err.message || "儲存失敗，請稍後再試");
    },
  });

  function handleSaveProfile() {
    if (!editingProject) return;
    setProfileError(null);
    const nextKey = selectedAccountValue === SYSTEM_DEFAULT_VALUE ? null : (selectedAccountValue as AnalysisAccountProfileKey);
    updateProfileMutation.mutate({
      project_id: editingProject.project_id,
      analysis_account_profile_key: nextKey,
      expected_updated_at: editingProject.llm_profile_updated_at,
    });
  }

  const persistedAccountValue = editingProject?.analysis_account_profile_key || SYSTEM_DEFAULT_VALUE;
  const previewAccountKey = selectedAccountValue === SYSTEM_DEFAULT_VALUE
    ? accountProfiles.find(profile => profile.isDefault)?.accountKey
    : selectedAccountValue as AnalysisAccountProfileKey;
  const previewAccountProfile = accountProfiles.find(profile => profile.accountKey === previewAccountKey);
  const previewLlmProfile = previewAccountProfile
    ? llmProfiles.find(profile => profile.key === previewAccountProfile.llmProfileKey)
    : undefined;
  const currentConfigured = Boolean(previewAccountProfile?.dataforseoConfigured && previewAccountProfile?.llmConfigured);
  const selectionDirty = Boolean(editingProject && selectedAccountValue !== persistedAccountValue);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">專案管理</h1>
          <p className="text-muted-foreground">管理分析專案</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" /> 新增專案</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>新增專案</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>專案名稱</Label>
                <Input placeholder="輸入專案名稱" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>說明（選填）</Label>
                <Textarea placeholder="專案說明" value={description} onChange={e => setDescription(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>分析帳戶</Label>
                <Select value={newAccountValue} onValueChange={setNewAccountValue} disabled={accountProfilesLoading}>
                  <SelectTrigger><SelectValue placeholder="選擇分析帳戶" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SYSTEM_DEFAULT_VALUE}>系統預設</SelectItem>
                    {accountProfiles.map(profile => (
                      <SelectItem
                        key={profile.accountKey}
                        value={profile.accountKey}
                        disabled={!profile.dataforseoConfigured || !profile.llmConfigured}
                      >
                        {profile.accountKey === "personal"
                          ? "Personal（DataForSEO + OpenAI）"
                          : "Company（DataForSEO + CometAPI）"}
                        {!profile.dataforseoConfigured || !profile.llmConfigured ? "（尚未設定）" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => createMutation.mutate()} className="w-full" disabled={!name.trim() || createMutation.isPending}>
                {createMutation.isPending ? "建立中..." : "建立專案"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="text-center text-muted-foreground py-8">載入中...</div>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8 text-muted-foreground">
            尚無專案，點擊「新增專案」建立
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects.map(p => (
            <Card key={p.project_id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">{p.name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditSheet(p)}>
                          <Settings className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>編輯專案設定</TooltipContent>
                    </Tooltip>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => { if (confirm(`確定要刪除「${p.name}」？`)) deleteMutation.mutate(p.project_id); }}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {p.description && <CardDescription>{p.description}</CardDescription>}
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">建立時間: {new Date(p.created_at).toLocaleString("zh-TW")}</p>
                <Badge variant="secondary">{formatLlmBadge(p)}</Badge>
                <Button variant="outline" size="sm" className="w-full" onClick={() => navigate(`/keyword-groups?project=${p.project_id}`)}>
                  <Tags className="h-4 w-4 mr-2" />關鍵字群組<ArrowRight className="h-3 w-3 ml-auto" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Sheet open={editingProject !== null} onOpenChange={(next) => { if (!next) closeEditSheet(); }}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>編輯專案設定</SheetTitle>
          </SheetHeader>
          {editingProject && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>專案名稱</Label>
                <Input value={editingProject.name} disabled readOnly />
              </div>
              <div className="space-y-2">
                <Label>說明</Label>
                <Textarea value={editingProject.description || ""} disabled readOnly placeholder="（未設定）" />
              </div>
              <div className="space-y-2">
                <Label>分析帳戶</Label>
                <Select
                  value={selectedAccountValue}
                  onValueChange={setSelectedAccountValue}
                  disabled={accountProfilesLoading || updateProfileMutation.isPending}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選擇分析帳戶" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SYSTEM_DEFAULT_VALUE}>系統預設</SelectItem>
                    {accountProfiles.map(profile => (
                      <SelectItem key={profile.accountKey} value={profile.accountKey} disabled={!profile.dataforseoConfigured || !profile.llmConfigured}>
                        {profile.accountKey === "personal" ? "Personal（DataForSEO + OpenAI）" : "Company（DataForSEO + CometAPI）"}{!profile.dataforseoConfigured || !profile.llmConfigured ? "（尚未設定）" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="rounded-md border p-3 text-sm text-muted-foreground space-y-1">
                <div>DataForSEO：{previewAccountKey === "company" ? "Company" : "Personal"}</div>
                <div>LLM Provider：{previewLlmProfile?.provider === "cometapi" ? "CometAPI" : "OpenAI"}</div>
                <div>帳戶：{previewAccountKey === "company" ? "Company" : "Personal"}</div>
                <div>已設定：{currentConfigured ? "是" : "尚未設定"}</div>
                <div>
                  來源：{selectionDirty
                    ? "待儲存"
                    : editingProject.llm_profile_source === "explicit"
                      ? "專案指定"
                      : editingProject.llm_profile_source === "legacy_account_map"
                        ? "舊版對應規則"
                        : "系統預設"}
                  （{selectionDirty ? "儲存後" : "目前"}解析為 {previewLlmProfile?.label || profileLabel(editingProject.resolved_llm_profile_key, llmProfiles)}）
                </div>
              </div>
              {profileError && (
                <p className="text-sm text-destructive">{profileError}</p>
              )}
            </div>
          )}
          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={closeEditSheet} disabled={updateProfileMutation.isPending}>取消</Button>
            <Button onClick={handleSaveProfile} disabled={updateProfileMutation.isPending || llmProfilesLoading}>
              {updateProfileMutation.isPending ? "儲存中..." : "儲存設定"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
